import { usersAccountLinkingCodeCreate } from "@sidegigllc/api/client";
import { env } from "config/env";
import { readAccountLinkingCode, stashAccountLinkingCode } from "domains/auth/accountLinking";
import { getForwardingParams } from "domains/auth/queries/helpers";
import type { ExtraState } from "domains/auth/queries/types";
import SuperTokens from "supertokens-web-js";
import Passwordless from "supertokens-web-js/recipe/passwordless";
import Session from "supertokens-web-js/recipe/session";
import ThirdParty from "supertokens-web-js/recipe/thirdparty";
import type { SetOptional } from "type-fest";

let initialized = false;

/**
 * Whether a session exists. In automation the SuperTokens client session is never populated (auth is
 * an injected header token, not a client session), so `Session.doesSessionExist()` returns a false
 * negative — bypass to `true`. Use this everywhere instead of calling `doesSessionExist()` directly.
 */
export const sessionExists = async (): Promise<boolean> =>
  env.IS_AUTOMATION || (initialized && Session.doesSessionExist());

const getBaseDomain = (domain: string) => new URL(domain).hostname.split(".").slice(-2).join(".");

const domainReplaceLookup: Record<string, string> = {
  "app.gigpro.com": "biz.qwick.com",
  "biz.qwick.com": "app.gigpro.com",
};
const replacementStrings = Object.keys(domainReplaceLookup);

export function initializeAuth() {
  if (initialized) return;
  // e.g. https://gp-api-dev.gigpro.com/ => gigpro.com
  const href = globalThis.location.href;
  const baseApiDomain = getBaseDomain(env.REACT_APP_SG_API_URL);
  const baseWebDomain = getBaseDomain(globalThis.location.origin);

  if (baseApiDomain !== baseWebDomain) {
    // check lookup for domain replacement
    const replacementDomain = replacementStrings.find((domain) => href.includes(domain));
    if (replacementDomain) {
      globalThis.location.href = href.replace(replacementDomain, domainReplaceLookup[replacementDomain]);
      return;
    }
    globalThis.location.href = href.replace(baseWebDomain, baseApiDomain);
    return;
  }

  initialized = true;
  SuperTokens.init({
    appInfo: {
      appName: "Gigpro",
      apiDomain: env.REACT_APP_SG_API_URL,
      apiBasePath: "/api/auth",
    },
    recipeList: [
      Session.init({
        // Pin both the backend (HttpOnly) and the JS-readable (sFrontToken, st-last-access-token-update)
        // cookies to the eTLD+1 (e.g. local.gigpro.com => .gigpro.com) so every app under that apex
        // (biz.qwick.com, data.qwick.com, etc.) sees the same session state. Without
        // sessionTokenFrontendDomain, supertokens-web-js writes sFrontToken host-only on the current
        // origin, which means data.qwick.com can't clear or observe a stale sFrontToken set by biz,
        // producing the incognito redirect loop tracked in bi-dashboard/INCOGNITO_LOOP_DEBUG.md.
        sessionTokenBackendDomain: `.${baseApiDomain}`,
        sessionTokenFrontendDomain: `.${baseApiDomain}`,
      }),
      Passwordless.init({ preAPIHook }),
      ThirdParty.init({
        preAPIHook,
        override: {
          functions: (origImpl) => ({
            ...origImpl,
            setStateAndOtherInfoToStorage(input) {
              const modifiedInput = {
                ...input,
                state: { ...input.state, search: globalThis.location.search },
              };
              return origImpl.setStateAndOtherInfoToStorage(modifiedInput);
            },
            signInAndUp(input) {
              const state = origImpl.getStateAndOtherInfoFromStorage<ExtraState>(input);
              return origImpl.signInAndUp({
                ...input,
                options: { preAPIHook: (context) => preAPIHook(context, getForwardingParams(state?.search)) },
              });
            },
          }),
        },
      }),
    ],
  });
}

// A header, not a query param: proxies log query strings. Only the passwordless callback reads it.
const ACCOUNT_LINKING_CODE_HEADER = "X-Gigpro-Account-Linking-Code";

export function preAPIHook(
  { action, requestInit, url }: SetOptional<ThirdParty.PreAPIHookContext | Passwordless.PreAPIHookContext, "action">,
  forwardParams = getForwardingParams(),
) {
  const queryIndex = url.indexOf("?");
  const apiParams = new URLSearchParams(queryIndex === -1 ? undefined : url.slice(queryIndex + 1));

  for (const [key, value] of forwardParams) {
    apiParams.set(key, value);
  }

  const linkingCode = action === "PASSWORDLESS_CONSUME_CODE" ? readAccountLinkingCode() : undefined;
  const finalRequestInit = linkingCode
    ? { ...requestInit, headers: { ...requestInit.headers, [ACCOUNT_LINKING_CODE_HEADER]: linkingCode } }
    : requestInit;

  const baseUrl = queryIndex === -1 ? url : url.slice(0, queryIndex);
  return Promise.resolve({ requestInit: finalRequestInit, url: `${baseUrl}?${apiParams}` });
}

export async function startAccountLinking() {
  // Mint before signing out: the endpoint needs this session.
  const stashed = stashAccountLinkingCode(await usersAccountLinkingCodeCreate("me"));
  if (!stashed) throw new Error("Couldn't start account linking. Please try again.");
  await Session.signOut();
  globalThis.location.href = "/auth";
}
